import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Storage keys
const STORAGE_KEYS = {
  lastShown: "mathallen_popup_last_shown",
  sessionShown: "mathallen_popup_session_shown",
  neverShow: "mathallen_popup_never_show"
};

export default function PromoPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if device is mobile
  const isMobile = () => {
    return window.innerWidth < 768;
  };

  // Check if popup should be shown based on frequency
  const shouldShowBasedOnFrequency = (frequency) => {
    switch (frequency) {
      case "once":
        // Show only once ever
        return !localStorage.getItem(STORAGE_KEYS.neverShow);
      
      case "daily":
        // Show once per day
        const lastShown = localStorage.getItem(STORAGE_KEYS.lastShown);
        if (!lastShown) return true;
        const lastDate = new Date(lastShown).toDateString();
        const today = new Date().toDateString();
        return lastDate !== today;
      
      case "session":
        // Show once per session
        return !sessionStorage.getItem(STORAGE_KEYS.sessionShown);
      
      case "always":
      default:
        // Show on every page load
        return true;
    }
  };

  // Check if popup should be shown on current page
  const shouldShowOnPage = (pages, currentPath) => {
    // If no pages specified, show on all pages
    if (!pages || pages.length === 0) return true;
    
    // Check if current path matches any of the specified pages
    return pages.some(page => {
      if (page === "/") return currentPath === "/";
      return currentPath.startsWith(page);
    });
  };

  // Mark popup as shown
  const markAsShown = (frequency) => {
    switch (frequency) {
      case "once":
        localStorage.setItem(STORAGE_KEYS.neverShow, "true");
        break;
      case "daily":
        localStorage.setItem(STORAGE_KEYS.lastShown, new Date().toISOString());
        break;
      case "session":
        sessionStorage.setItem(STORAGE_KEYS.sessionShown, "true");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    // Don't show popup on admin pages
    if (location.pathname.startsWith('/admin')) {
      setLoading(false);
      return;
    }

    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        const data = response.data;
        setSettings(data);
        
        // Check all conditions
        const isEnabled = data.popup_enabled === true;
        const frequencyOk = shouldShowBasedOnFrequency(data.popup_frequency || "always");
        const pageOk = shouldShowOnPage(data.popup_pages, location.pathname);
        const deviceOk = isMobile() ? data.popup_show_mobile !== false : data.popup_show_desktop !== false;
        
        // Debug
        console.log("Popup check:", { isEnabled, frequencyOk, pageOk, deviceOk, settings: data });
        
        if (isEnabled && frequencyOk && pageOk && deviceOk) {
          const delay = (data.popup_delay || 1) * 1000;
          setTimeout(() => {
            setIsOpen(true);
            markAsShown(data.popup_frequency || "always");
          }, delay);
        }
      } catch (error) {
        console.error("Error fetching popup settings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [location.pathname]);

  const handleClose = (e) => {
    e.stopPropagation();
    setIsOpen(false);
  };

  const handleClick = () => {
    setIsOpen(false);
    const link = settings?.popup_link || "/erbjudanden";
    
    // Check if external link
    if (link.startsWith('http')) {
      window.open(link, '_blank');
    } else {
      navigate(link);
    }
  };

  if (loading || !isOpen || !settings) return null;

  // Handle image URL - check if it's an API path or external URL
  let imageUrl = settings?.popup_image_url || "https://customer-assets.emergentagent.com/job_stormarknad-malmo/artifacts/mg8le9jb_Giris%CC%A7%20Tasar%C4%B1m%C4%B1.png";
  if (imageUrl.startsWith('/api')) {
    imageUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
  }

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={handleClose}
      data-testid="promo-popup-overlay"
    >
      <div 
        className="relative max-w-lg w-full animate-scale-in cursor-pointer"
        onClick={(e) => e.stopPropagation()}
        data-testid="promo-popup-container"
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute -top-3 -right-3 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
          data-testid="promo-popup-close"
          aria-label="Stäng"
        >
          <X className="w-5 h-5 text-gray-700" />
        </button>

        {/* Popup Image */}
        <div 
          onClick={handleClick}
          className="rounded-2xl overflow-hidden shadow-2xl hover:scale-[1.02] transition-transform duration-300"
        >
          <img
            src={imageUrl}
            alt="Veckans erbjudanden på Mathallen Malmö"
            className="w-full h-auto"
            data-testid="promo-popup-image"
          />
        </div>

        {/* Click hint for mobile */}
        <p className="text-center text-white/80 text-sm mt-3 md:hidden">
          Tryck på bilden för att se erbjudanden
        </p>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { 
            opacity: 0; 
            transform: scale(0.9); 
          }
          to { 
            opacity: 1; 
            transform: scale(1); 
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
